'use client';

import React, { useRef, useEffect } from 'react';
import Link from 'next/link';

const deals = [
  {
    title: 'Commercial Real Estate',
    short: 'CRE',
    description:
      'Fund refundable EMDs for office, industrial, retail, hospitality, land, and other commercial acquisitions.',
    tags: ['Office', 'Industrial', 'Retail / NNN', 'Hospitality'],
    color: '#52B788',
    textColor: 'var(--color-green-text)',
    primary: true,
  },
  {
    title: 'Multifamily',
    short: 'MF',
    description:
      'Keep capital available while securing apartment and multifamily opportunities during due diligence.',
    tags: ['Apartments', 'Value-Add', 'Portfolio', 'Concurrent Deals'],
    color: '#74C69D',
    textColor: 'var(--color-green-text)',
    primary: true,
  },
  {
    title: '1031 Exchanges',
    short: '1031',
    description:
      'Secure replacement properties while exchange proceeds remain with your Qualified Intermediary.',
    tags: ['Replacement Property', 'QI Timeline', 'Exchange Deposits'],
    color: '#C9A84C',
    textColor: 'var(--color-gold-text)',
    primary: false,
  },
  {
    title: 'Oil & Gas',
    short: 'Energy',
    description:
      'Preserve liquidity across eligible mineral, acreage, royalty, and energy asset opportunities.',
    tags: ['Mineral Rights', 'Royalty Interests', 'Acreage', 'Upstream'],
    color: '#E9C46A',
    textColor: 'var(--color-energy-text)',
    primary: false,
  },
  {
    title: 'Business Acquisitions / M&A',
    short: 'M&A',
    description:
      'Secure acquisition opportunities while diligence and transaction structuring are underway.',
    tags: ['Search Funds', 'LOI Deposits', 'Owner-Operator', 'PE Add-Ons'],
    color: '#C9A84C',
    textColor: 'var(--color-gold-text)',
    primary: false,
  },
  {
    title: 'Luxury Real Estate Investments',
    short: 'Luxury RE',
    description:
      'Evaluate multiple high-value residential investment properties without funding every refundable deposit yourself.',
    tags: ['High-Value Properties', 'Multiple Acquisitions', 'Portfolio-Building'],
    color: '#52B788',
    textColor: 'var(--color-green-text)',
    primary: false,
  },
];

interface Deal {
  title: string;
  short: string;
  description: string;
  tags: string[];
  color: string;
  textColor?: string;
  primary: boolean;
}

const featuredDeals = deals.filter((d) => d.primary) as Deal[];
const subDeals = deals.filter((d) => !d.primary) as Deal[];

export default function IndustriesShowcase() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('[data-reveal]');
            els.forEach((el, i) => {
              const element = el as HTMLElement;
              setTimeout(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
                element.style.filter = 'blur(0)';
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="industries"
      className="py-20 lg:py-24 px-6 relative z-10"
      style={{ background: 'var(--color-bg)', borderTop: '4px solid #0D1F17' }}
      aria-label="Deals funded by EarnestBridge"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header — left-aligned, editorial */}
        <div
          data-reveal
          className="mb-12 lg:mb-14"
          style={{
            opacity: 0,
            transform: 'translateY(20px)',
            filter: 'blur(4px)',
            transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-5 h-px" style={{ background: 'rgba(201,168,76,0.5)' }} />
            <p className="section-label">Deals We Fund</p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2
              className="font-display font-semibold"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-foreground)',
                letterSpacing: '-0.025em',
                lineHeight: 1.1,
              }}
            >
              Refundable EMD Solutions for{' '}
              <span className="italic" style={{ color: 'var(--color-primary)' }}>
                High-Value Transactions
              </span>
            </h2>
            <p
              className="text-sm leading-relaxed md:max-w-xs"
              style={{ color: 'var(--color-muted)' }}
            >
              Wherever a deal requires a refundable deposit, we fund it.
            </p>
          </div>
        </div>

        {/* ── Primary Deal Categories — asymmetric editorial panels ── */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 mb-0"
          style={{ borderTop: '1px solid rgba(27,67,50,0.1)' }}
        >
          {featuredDeals.map((deal, i) => (
            <div
              key={deal.title}
              data-reveal
              className="group"
              style={{
                opacity: 0,
                transform: 'translateY(20px)',
                filter: 'blur(4px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
                borderRight: i === 0 ? '1px solid rgba(27,67,50,0.1)' : 'none',
                borderBottom: '1px solid rgba(27,67,50,0.1)',
              }}
            >
              <div className="p-7 xl:p-10 h-full">
                {/* Primary label */}
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="text-[9px] font-bold uppercase tracking-[0.32em]"
                    style={{ color: deal.color }}
                  >
                    Primary Category
                  </span>
                  <div className="flex-1 h-px" style={{ background: `${deal.color}30` }} />
                </div>

                {/* Short label */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center"
                    style={{
                      border: `1px solid ${deal.color}30`,
                      background: `${deal.color}0D`,
                    }}
                  >
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.28em]"
                      style={{ color: deal.textColor ?? deal.color }}
                    >
                      {deal.short}
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h3
                  className="font-display font-semibold mb-3 leading-tight"
                  style={{
                    fontSize: 'clamp(1.25rem, 2.5vw, 1.625rem)',
                    color: 'var(--color-foreground)',
                    letterSpacing: '-0.015em',
                  }}
                >
                  {deal.title}
                </h3>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed mb-6"
                  style={{ color: 'var(--color-muted)', maxWidth: '44ch' }}
                >
                  {deal.description}
                </p>

                {/* Tags */}
                <ul className="flex flex-wrap gap-x-4 gap-y-1.5" aria-label={`${deal.title} deal types`}>
                  {deal.tags.map((tag) => (
                    <li
                      key={tag}
                      className="text-[10px] font-semibold uppercase tracking-[0.14em]"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* ── Secondary Deal Categories — compact structured rows ── */}
        {/* Desktop: 4-column grid */}
        <div
          className="hidden md:grid md:grid-cols-2 lg:grid-cols-4"
          style={{ borderBottom: '1px solid rgba(27,67,50,0.1)' }}
        >
          {subDeals.map((deal, i) => (
            <div
              key={deal.title}
              data-reveal
              style={{
                opacity: 0,
                transform: 'translateY(16px)',
                filter: 'blur(4px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                borderRight: i < subDeals.length - 1 ? '1px solid rgba(27,67,50,0.08)' : 'none',
                borderTop: '1px solid rgba(27,67,50,0.08)',
              }}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="text-[9px] font-bold uppercase tracking-[0.28em]"
                    style={{ color: deal.textColor ?? deal.color }}
                  >
                    {deal.short}
                  </span>
                </div>
                <h3
                  className="font-display font-semibold mb-2 leading-tight"
                  style={{
                    fontSize: '0.9375rem',
                    color: 'var(--color-foreground)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {deal.title}
                </h3>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {deal.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: compact list for secondary deals */}
        <div
          className="md:hidden"
          style={{ borderTop: '1px solid rgba(27,67,50,0.1)' }}
        >
          {subDeals.map((deal, i) => (
            <div
              key={deal.title}
              data-reveal
              className="flex items-start gap-4 py-4"
              style={{
                opacity: 0,
                transform: 'translateY(12px)',
                filter: 'blur(4px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                borderBottom: i < subDeals.length - 1 ? '1px solid rgba(27,67,50,0.08)' : 'none',
              }}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3
                    className="font-display font-semibold leading-tight"
                    style={{ fontSize: '0.9375rem', color: 'var(--color-foreground)', letterSpacing: '-0.01em' }}
                  >
                    {deal.title}
                  </h3>
                  <span
                    className="text-[9px] font-bold uppercase tracking-[0.22em] flex-shrink-0"
                    style={{ color: deal.textColor ?? deal.color }}
                  >
                    {deal.short}
                  </span>
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {deal.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA row */}
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-10"
          data-reveal
          style={{
            opacity: 0,
            transform: 'translateY(12px)',
            filter: 'blur(4px)',
            transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <Link
            href="/industries"
            className="inline-block text-[10px] font-bold uppercase tracking-[0.22em] px-7 py-3.5 transition-all duration-200"
            style={{
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: '6px',
              minHeight: '44px',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Explore Deals We Fund
            <span aria-hidden="true"> →</span>
          </Link>
          <Link
            href="/contact"
            className="text-[10px] font-bold uppercase tracking-[0.22em] transition-all duration-200"
            style={{
              color: 'var(--color-primary)',
              borderBottom: '1px solid rgba(27,67,50,0.3)',
              paddingBottom: '3px',
            }}
          >
            Submit Your Deal
          </Link>
        </div>
      </div>
    </section>
  );
}
